export interface SwitcherProps {
  id: string;
  checked: boolean;
  label: React.ReactNode;
  disabled?: boolean;
  onChange: () => void;
}

/**
 * Switcher component based on https://flowbite.com/docs/components/forms/#toggle-switch
 */
export const Switcher: React.FC<SwitcherProps> = ({ id, disabled, checked, label, onChange }) => {
  const addedStyle: React.CSSProperties = { minWidth: "44px" };
  const labelStyle: React.CSSProperties = { whiteSpace: "nowrap" };

  if (disabled) {
    const disabledClassNames = [
      "w-11",
      "h-6",
      "bg-neutral-200",
      "rounded-full",
      "peer",
      "dark:bg-neutral-700",
      "peer-checked:after:translate-x-full",
      "peer-checked:after:border-white",
      "after:content-['']",
      "after:absolute",
      "after:top-0.5",
      "after:left-[2px]",
      "after:bg-white",
      "after:border-neutral-300",
      "after:border",
      "after:rounded-full",
      "after:h-5",
      "after:w-5",
      "after:transition-all",
      "dark:border-neutral-600",
      "peer-checked:bg-primary-550",
    ].join(" ");

    return (
      <label htmlFor={id} className="inline-flex relative items-center mb-3 cursor-pointer">
        <input
          type="checkbox"
          value=""
          id={id}
          className="sr-only peer"
          onChange={onChange}
          disabled
          checked={checked}
        />
        <div style={addedStyle} className={disabledClassNames} />
        <span style={labelStyle} className="ml-3 text-sm font-medium text-neutral-400 dark:text-neutral-500">
          {label}
        </span>
      </label>
    );
  }

  if (checked) {
    const checkedClassNames = [
      "w-11",
      "h-6",
      "bg-neutral-200",
      "rounded-full",
      "peer",
      // "peer-focus:ring-4",
      // "peer-focus:ring-primary-300",
      // "dark:peer-focus:ring-primary-550",
      "dark:bg-neutral-700",
      "peer-checked:after:translate-x-full",
      "peer-checked:after:border-white",
      "after:content-['']",
      "after:absolute",
      "after:top-0.5",
      "after:left-[2px]",
      "after:bg-white",
      "after:border-neutral-300",
      "after:border",
      "after:rounded-full",
      "after:h-5",
      "after:w-5",
      "after:transition-all",
      "dark:border-neutral-600",
      "peer-checked:bg-primary-300",
    ].join(" ");

    return (
      <label htmlFor={id} className="inline-flex relative items-center mb-4 cursor-pointer">
        <input type="checkbox" value="" id={id} className="sr-only peer" onChange={onChange} checked={checked} />
        <div style={addedStyle} className={checkedClassNames} />
        <span style={labelStyle} className="ml-3 text-sm font-medium text-neutral-900 dark:text-neutral-300">
          {label}
        </span>
      </label>
    );
  }

  const nonCheckedClassNames = [
    "w-11",
    "h-6",
    "bg-neutral-200",
    "rounded-full",
    "peer",
    // "peer-focus:ring-4",
    // "peer-focus:ring-primary-300",
    // "dark:peer-focus:ring-primary-550",
    "dark:bg-neutral-700",
    "peer-checked:after:translate-x-full",
    "peer-checked:after:border-white",
    "after:content-['']",
    "after:absolute",
    "after:top-0.5",
    "after:left-[2px]",
    "after:bg-white",
    "after:border-neutral-300",
    "after:border",
    "after:rounded-full",
    "after:h-5",
    "after:w-5",
    "after:transition-all",
    "dark:border-neutral-600",
    "peer-checked:bg-primary-300",
  ].join(" ");

  return (
    <label htmlFor={id} className="inline-flex relative items-center mb-4 cursor-pointer">
      <input type="checkbox" value="" id={id} className="sr-only peer" onChange={onChange} checked={checked} />
      <div style={addedStyle} className={nonCheckedClassNames} />
      <span style={labelStyle} className="ml-3 text-sm font-medium text-neutral-900 dark:text-neutral-300">
        {label}
      </span>
    </label>
  );
};
